import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../context/ThemeContext";
import icons from "../../utils/icons";
import constants from "../../utils/constants";
import { isAuthenticated } from "../../services/authService";
import ThemeToggle from "../../components/ThemeToggle";
import api from "../../services/api";
import "./Home.scss";

export default function Home() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { PolygonIcon, EthereumIcon, TronIcon } = icons();
  const cryptoCurrencies = constants.cryptoCurrencies;

  const [gasFees, setGasFees] = useState([]);

  useEffect(() => {
    if (isAuthenticated()) {
      navigate("/dashboard");
    }
  }, [navigate]);

  useEffect(() => {
    const fetchRates = async () => {
      try {
        const res = await api("get", constants.endPoints.gasFees);
        if (res.success && res.data) {
          setGasFees(res.data);
        }
      } catch (e) {
        // Rates will use fallback
      }
    };
    fetchRates();
  }, []);

  const [transactionForm, setTransactionForm] = useState({
    amountPaid: "1000",
    paidAmountCurrency: "CAD",
    amountReceived: "735.00",
    receivedAmountCurrency: "USDT",
  });

  const getRate = (from, to) => {
    const fee = gasFees.find(f => f.firstCurrency === from && f.secondCurrency === to);
    if (fee) {
      const rate = fee.selectedExchangeRate === "Market" && fee.liveMarketRate
        ? fee.liveMarketRate
        : fee.exchangePricePerUnit;
      if (from === "CAD") {
        return rate ? 1 / rate : 0.735;
      }
      return rate || 1.36;
    }
    if (from === "CAD") {
      if (to === "USDT" || to === "USDC") return 0.735;
    } else if (from === "USDT" || from === "USDC") {
      if (to === "CAD") return 1.36;
    }
    return 1;
  };

  const calculateReceivedAmount = (amountPaid, paidCurrency, receivedCurrency) => {
    if (!amountPaid || isNaN(parseFloat(amountPaid))) return "";
    const rate = getRate(paidCurrency, receivedCurrency);
    return (parseFloat(amountPaid) * rate).toFixed(2);
  };

  const handleChange = ({ name, value }) => {
    setTransactionForm((prev) => {
      let newForm = { ...prev, [name]: value };

      if (name === "paidAmountCurrency") {
        if (value === "CAD") {
          if (prev.receivedAmountCurrency !== "USDT" && prev.receivedAmountCurrency !== "USDC") {
            newForm.receivedAmountCurrency = "USDT";
          }
        } else {
          newForm.receivedAmountCurrency = "CAD";
        }
      }

      if (name === "receivedAmountCurrency") {
        if (value === "CAD") {
          if (prev.paidAmountCurrency !== "USDT" && prev.paidAmountCurrency !== "USDC") {
            newForm.paidAmountCurrency = "USDT";
          }
        } else {
          newForm.paidAmountCurrency = "CAD";
        }
      }

      newForm.amountReceived = calculateReceivedAmount(
        newForm.amountPaid,
        newForm.paidAmountCurrency,
        newForm.receivedAmountCurrency
      );

      return newForm;
    });
  };

  const handleSwap = () => {
    setTransactionForm((prev) => {
      const newPaidCurrency = prev.receivedAmountCurrency;
      const newReceivedCurrency = prev.paidAmountCurrency;
      const newPaidAmount = prev.amountReceived;
      const newReceivedAmount = calculateReceivedAmount(
        newPaidAmount,
        newPaidCurrency,
        newReceivedCurrency
      );

      return {
        amountPaid: newPaidAmount,
        paidAmountCurrency: newPaidCurrency,
        amountReceived: newReceivedAmount,
        receivedAmountCurrency: newReceivedCurrency,
      };
    });
  };

  const onClickGetStarted = () => {
    isAuthenticated() ? navigate("/dashboard") : navigate("/sign-in");
  };

  const onClickSwapNow = () => {
    if (isAuthenticated()) {
      navigate("/dashboard", { state: transactionForm });
    } else {
      navigate("/sign-in");
    }
  };

  return (
    <div className="home-page">
      {/* Animated Background */}
      <div className="gradient-bg"></div>

      {/* Floating Icons */}
      <div className="floating-icons">
        <div className="crypto-icon">₮</div>
        <div className="crypto-icon">$</div>
        <div className="crypto-icon">◈</div>
        <div className="crypto-icon">⛓️</div>
        <div className="crypto-icon">⚡</div>
      </div>

      {/* Navbar */}
      <nav className="home-navbar">
        <div className="home-logo">tinbox</div>
        <div className="home-nav-links">
          <a href="#features">Features</a>
          <a href="#how-it-works">How It Works</a>
          <a href="#contact">Contact</a>
          <ThemeToggle />
          <button
            className="btn btn-outline"
            onClick={() => navigate("/sign-in")}
          >
            Log In
          </button>
          <button
            className="btn btn-primary"
            onClick={onClickGetStarted}
          >
            Get Started
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-badge">
            <span>🚀</span> Stablecoins on Multiple Blockchains
          </div>
          <h1>
            Exchange CAD to <span className="gradient-text">Stablecoins</span> Instantly
          </h1>
          <p>
            The fastest way to buy and sell stablecoins (USDT, USDC) in Canada.
            Low fees, secure transfers, and instant delivery to your wallet.
          </p>
          <div className="hero-buttons">
            <button className="btn btn-primary btn-lg" onClick={onClickGetStarted}>
              Start Trading
            </button>
            <button className="btn btn-outline btn-lg" onClick={() => navigate("/about-us")}>
              Learn More
            </button>
          </div>
        </div>

        {/* Calculator Card */}
        <div className="hero-visual">
          <div className="card-stack">
            <div className="glass-card card-back-2"></div>
            <div className="glass-card card-back-1"></div>

            <div className="glass-card card-main">
              <div className="calc-header">
                <span style={{ fontWeight: 600 }}>💱 Quick Calculator</span>
              </div>

              <div className="mock-input">
                <div className="input-group">
                  <span className="input-label">You Send</span>
                  <input
                    type="number"
                    className="input-field"
                    value={transactionForm.amountPaid}
                    onChange={(e) => handleChange({ name: "amountPaid", value: e.target.value })}
                  />
                </div>
                <select
                  className="currency-select"
                  value={transactionForm.paidAmountCurrency}
                  onChange={(e) => handleChange({ name: "paidAmountCurrency", value: e.target.value })}
                >
                  <option value="CAD">CAD</option>
                  <option value="USDT">USDT</option>
                  <option value="USDC">USDC</option>
                </select>
              </div>

              <div className="calculator-divider">
                <button type="button" className="swap-btn" onClick={handleSwap} title="Swap Currencies">
                  <i className="pi pi-sync"></i>
                </button>
              </div>

              <div className="mock-input">
                <div className="input-group">
                  <span className="input-label">You Receive</span>
                  <input
                    type="text"
                    className="input-field"
                    value={transactionForm.amountReceived}
                    readOnly
                    style={{ color: "var(--primary-color)" }}
                  />
                </div>
                <select
                  className="currency-select"
                  value={transactionForm.receivedAmountCurrency}
                  onChange={(e) => handleChange({ name: "receivedAmountCurrency", value: e.target.value })}
                >
                  {transactionForm.paidAmountCurrency === "CAD" ? (
                    <>
                      <option value="USDT">USDT</option>
                      <option value="USDC">USDC</option>
                    </>
                  ) : (
                    <option value="CAD">CAD</option>
                  )}
                </select>
              </div>

              <button className="rate-pill" onClick={onClickSwapNow}>
                ⚡ Calculate Rate
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <div className="trust-bar">
        <div className="trust-item">
          <div className="trust-icon">🔒</div>
          <span>Bank-Level Security</span>
        </div>
        <div className="trust-item">
          <div className="trust-icon">⚡</div>
          <span>Interac e-Transfer</span>
        </div>
        <div className="trust-item">
          <div className="trust-icon">✓</div>
          <span>FINTRAC Registered</span>
        </div>
        <div className="trust-item">
          <div className="trust-icon">🛡️</div>
          <span>256-bit Encryption</span>
        </div>
      </div>

      {/* Stats Section */}
      <section className="stats-section" id="features">
        <div className="stats-grid">
          <div className="stat-item">
            <h3>$52.3M</h3>
            <p>Volume Traded</p>
          </div>
          <div className="stat-item">
            <h3>12,847</h3>
            <p>Happy Users</p>
          </div>
          <div className="stat-item">
            <h3>0.5%</h3>
            <p>Lowest Fees</p>
          </div>
          <div className="stat-item">
            <h3>&lt;4 min</h3>
            <p>Avg. Transfer Time</p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="section-header">
          <h2>Why Choose Tinbox?</h2>
          <p>Trusted by thousands for fast, secure stablecoin exchanges</p>
        </div>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">⚡</div>
            <h3>Lightning Fast</h3>
            <p>Complete your exchange in minutes with our streamlined process and instant wallet transfers.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🛡️</div>
            <h3>Bank-Level Security</h3>
            <p>Your funds are protected with industry-leading encryption and secure e-transfer protocols.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">💰</div>
            <h3>Low Fees</h3>
            <p>Transparent pricing with no hidden fees. See exactly what you'll receive before you trade.</p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works" id="how-it-works">
        <div className="section-header">
          <h2>How It Works</h2>
          <p>Get started in three simple steps</p>
        </div>
        <div className="steps">
          <div className="step">
            <div className="step-number">1</div>
            <h3>Verify Identity</h3>
            <p>Quick KYC verification to keep your account secure</p>
          </div>
          <div className="step">
            <div className="step-number">2</div>
            <h3>Send E-Transfer</h3>
            <p>Send CAD via Interac e-Transfer from your bank</p>
          </div>
          <div className="step">
            <div className="step-number">3</div>
            <h3>Receive Stablecoins</h3>
            <p>USDT/USDC sent directly to your wallet within minutes</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-box">
          <h2>Ready to Start?</h2>
          <p>Join thousands of Canadians exchanging CAD for stablecoins securely</p>
          <button className="btn btn-white btn-lg" onClick={onClickGetStarted}>
            Create Free Account
          </button>
        </div>
      </section>

    </div>
  );
}